import { useEffect, type RefObject } from 'react'
import type { ItineraryScrollTarget } from './useItineraryUpdates'

interface ItineraryScrollTargetSelectors {
  item?: (target: ItineraryScrollTarget) => string | undefined
  day?: (target: ItineraryScrollTarget) => string | undefined
  stop: (target: ItineraryScrollTarget) => string | undefined
}

interface UseItineraryScrollTargetParams {
  scrollTarget: ItineraryScrollTarget | undefined
  containerRef: RefObject<HTMLElement | null>
  selectors: ItineraryScrollTargetSelectors
  onScrollComplete?: () => void
  maxRetries?: number
}

function queryScrollElement(container: HTMLElement, selector: string | undefined): HTMLElement | undefined {
  if (!selector) return undefined
  return container.querySelector<HTMLElement>(selector) ?? undefined
}

export function useItineraryScrollTarget({
  scrollTarget,
  containerRef,
  selectors,
  onScrollComplete,
  maxRetries = 5,
}: UseItineraryScrollTargetParams) {
  useEffect(() => {
    if (scrollTarget == null || !containerRef.current) return

    let rafId: number
    let retryCount = 0

    const attemptScroll = () => {
      const container = containerRef.current
      if (!container) {
        onScrollComplete?.()
        return
      }

      const itemEl = queryScrollElement(container, selectors.item?.(scrollTarget))
      if (itemEl) {
        itemEl.scrollIntoView({ behavior: 'smooth', block: 'center' })
        onScrollComplete?.()
        return
      }

      if (scrollTarget.itemId !== undefined && retryCount < maxRetries) {
        retryCount++
        rafId = requestAnimationFrame(attemptScroll)
        return
      }

      const dayEl = queryScrollElement(container, selectors.day?.(scrollTarget))
      if (dayEl) {
        dayEl.scrollIntoView({ behavior: 'smooth', block: 'start' })
        onScrollComplete?.()
        return
      }

      const stopEl = queryScrollElement(container, selectors.stop(scrollTarget))
      if (stopEl) {
        stopEl.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }
      onScrollComplete?.()
    }

    rafId = requestAnimationFrame(attemptScroll)

    return () => {
      cancelAnimationFrame(rafId)
    }
  }, [containerRef, maxRetries, onScrollComplete, scrollTarget, selectors])
}
